import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Eye, EyeOff, Trash2, ExternalLink, Save, X, FileText, Archive } from "lucide-react";
import { draftKey, saveDraft, loadDraft, clearDraft, formatDraftAge } from "../lib/draft";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function Articles() {
  const [items, setItems] = React.useState(null);
  const [editing, setEditing] = React.useState(null);   // full article object or {isNew:true, ...}
  const [busy, setBusy] = React.useState(false);
  const [draftMeta, setDraftMeta] = React.useState(null);   // {saved_at} when autosave wrote

  // The key we use to autosave the currently-open editor. `new` for unsaved,
  // otherwise keyed by slug so edits to different articles don't collide.
  const currentKey = React.useMemo(() => {
    if (!editing) return null;
    return draftKey("articles", editing.isNew ? "new" : editing.slug);
  }, [editing]);

  // Autosave the editor state on every change (debounced).
  React.useEffect(() => {
    if (!editing || !currentKey) return;
    const t = setTimeout(() => {
      const snapshot = {
        isNew: !!editing.isNew,
        slug: editing.slug || null,
        title: editing.title || "",
        body_markdown: editing.body_markdown || "",
        summary: editing.summary || "",
        tags: editing.tags || [],
        voice: editing.voice || "gabrielle",
        kind: editing.kind || "essay",
      };
      saveDraft(currentKey, snapshot);
      setDraftMeta({ saved_at: Date.now() });
    }, 800);
    return () => clearTimeout(t);
  }, [editing, currentKey]);

  const load = async () => {
    try {
      const { data } = await api.get("/articles");
      setItems(data);
    } catch {
      toast.error("Couldn't load articles.");
    }
  };
  React.useEffect(() => { load(); }, []);

  // Any orphan drafts from previous session (we only auto-restore the "new" one;
  // per-slug drafts are surfaced when user opens that article).
  const pendingNewDraft = React.useMemo(() => loadDraft(draftKey("articles", "new")), [items]);

  const newArticle = () => {
    // If a restored-in-progress draft exists, offer to pick it up.
    const existing = loadDraft(draftKey("articles", "new"));
    if (existing && existing.data) {
      setEditing({ ...existing.data, isNew: true });
      setDraftMeta({ saved_at: existing.saved_at });
      toast.info("Restored your unsaved draft.");
      return;
    }
    setEditing({
      isNew: true, title: "", body_markdown: "", summary: "", tags: [],
      voice: "gabrielle", kind: "essay",
    });
    setDraftMeta(null);
  };

  const openExisting = (a) => {
    const existing = loadDraft(draftKey("articles", a.slug));
    if (existing && existing.data) {
      const choice = window.confirm(
        `You have unsaved local edits from ${formatDraftAge(existing.saved_at)} for "${a.title}".\n\n` +
        `OK = restore unsaved edits\nCancel = discard local edits and load the server copy`
      );
      if (choice) {
        setEditing({ ...a, ...existing.data, slug: a.slug, isNew: false, published: a.published });
        setDraftMeta({ saved_at: existing.saved_at });
        return;
      }
      clearDraft(draftKey("articles", a.slug));
    }
    setEditing({ ...a });
    setDraftMeta(null);
  };

  const discardDraft = () => {
    if (!currentKey) return;
    clearDraft(currentKey);
    setDraftMeta(null);
    toast.info("Local draft discarded.");
  };

  const save = async (publishImmediately = false) => {
    if (!editing.title.trim() || editing.body_markdown.trim().length < 10) {
      return toast.error("Title and body required.");
    }
    setBusy(true);
    try {
      if (editing.isNew) {
        const { data } = await api.post("/articles", {
          title: editing.title,
          body_markdown: editing.body_markdown,
          summary: editing.summary,
          tags: editing.tags,
          voice: editing.voice,
          kind: editing.kind || "essay",
          publish: publishImmediately,
        });
        toast.success(publishImmediately ? "Published." : "Saved as draft.");
        if (currentKey) clearDraft(currentKey);
        setEditing(null);
        setDraftMeta(null);
        await load();
        return data;
      } else {
        await api.put(`/articles/${editing.slug}`, {
          title: editing.title,
          body_markdown: editing.body_markdown,
          summary: editing.summary,
          tags: editing.tags,
          voice: editing.voice,
          kind: editing.kind || "essay",
        });
        if (publishImmediately && !editing.published) {
          await api.post(`/articles/${editing.slug}/publish`);
        }
        toast.success("Saved.");
        if (currentKey) clearDraft(currentKey);
        setEditing(null);
        setDraftMeta(null);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed. Your work is safe — restored on next open.");
    } finally { setBusy(false); }
  };

  const togglePublish = async (a) => {
    try {
      if (a.published) {
        await api.post(`/articles/${a.slug}/unpublish`);
        toast.success("Unpublished.");
      } else {
        await api.post(`/articles/${a.slug}/publish`);
        toast.success("Published — now live on /essays.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    try {
      await api.delete(`/articles/${a.slug}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  if (editing) {
    return (
      <div data-testid="articles-editor" className="max-w-4xl">
        <div className="overline">Article · {editing.isNew ? "new" : "edit"}</div>
        <h1 className="font-display text-4xl mt-2 mb-2" style={{ color: "var(--elle-ink)" }}>
          {editing.isNew ? "New article" : editing.title || "Untitled"}
        </h1>
        <div className="mb-8 text-xs font-mono-elle flex items-center gap-3"
             data-testid="autosave-status"
             style={{ color: "var(--elle-ink-faint)" }}>
          <Archive size={12}/>
          <span>
            {draftMeta?.saved_at
              ? `Autosaved locally · ${formatDraftAge(draftMeta.saved_at)}`
              : "Autosave on — nothing lost if your session times out."}
          </span>
          {draftMeta?.saved_at && (
            <button type="button" onClick={discardDraft}
                    data-testid="autosave-discard"
                    className="underline hover:text-[var(--elle-amber)]">
              Discard local draft
            </button>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <label className="overline block mb-2">Title</label>
            <Input data-testid="article-title"
                   value={editing.title}
                   onChange={(e) => setEditing({...editing, title: e.target.value})}/>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="overline block mb-2">Kind</label>
              <select className="w-full bg-transparent border rounded-sm px-3 py-2 font-mono-elle text-sm"
                      style={{ borderColor: "var(--elle-line)", color: "var(--elle-ink)" }}
                      value={editing.kind || "essay"}
                      onChange={(e)=>setEditing({...editing, kind: e.target.value})}
                      data-testid="article-kind">
                <option value="essay">Essay (→ /essays)</option>
                <option value="excerpt">Excerpt (→ /excerpts)</option>
                <option value="short_story">Short story (→ /stories)</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Voice</label>
              <select className="w-full bg-transparent border rounded-sm px-3 py-2 font-mono-elle text-sm"
                      style={{ borderColor: "var(--elle-line)", color: "var(--elle-ink)" }}
                      value={editing.voice}
                      onChange={(e)=>setEditing({...editing, voice: e.target.value})}
                      data-testid="article-voice">
                <option value="gabrielle">Gabrielle (AI / markets / essays)</option>
                <option value="elle">Elle (author blog / craft / worldbuilding)</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Tags (comma-separated)</label>
              <Input data-testid="article-tags"
                     value={(editing.tags || []).join(", ")}
                     onChange={(e)=>setEditing({...editing,
                       tags: e.target.value.split(",").map(t=>t.trim()).filter(Boolean)})}
                     placeholder="AI, markets, commentary"/>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Summary <span className="text-xs" style={{color:"var(--elle-ink-faint)"}}>(optional — shown on listing)</span></label>
            <Textarea data-testid="article-summary"
                      rows={2} value={editing.summary || ""}
                      onChange={(e)=>setEditing({...editing, summary: e.target.value})}/>
          </div>
          <div>
            <label className="overline block mb-2">
              Body (Markdown — **bold**, *italic*, ## headings, &gt; quotes, - lists)
            </label>
            <Textarea data-testid="article-body"
                      className="min-h-[400px] font-mono-elle text-sm leading-relaxed"
                      value={editing.body_markdown}
                      onChange={(e)=>setEditing({...editing, body_markdown: e.target.value})}/>
            <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
              {(editing.body_markdown || "").split(/\s+/).filter(Boolean).length} words
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button data-testid="article-save-draft"
                  onClick={()=>save(false)} disabled={busy}
                  className="btn-ghost rounded-sm" variant="outline">
            <Save size={14} className="mr-2"/> Save draft
          </Button>
          <Button data-testid="article-publish"
                  onClick={()=>save(true)} disabled={busy}
                  className="btn-amber rounded-sm">
            <Eye size={14} className="mr-2"/> {editing.isNew || !editing.published ? "Save & publish" : "Save"}
          </Button>
          <Button onClick={()=>setEditing(null)} variant="outline" className="btn-ghost rounded-sm">
            <X size={14} className="mr-2"/> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="articles-page" className="max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline">Articles</div>
          <h1 className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
            The repository
          </h1>
          <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--elle-ink-dim)" }}>
            Long-form writing. Drafts stay here. Publishing pushes to{" "}
            <a href="/essays" target="_blank" rel="noreferrer"
               className="underline" style={{ color: "var(--elle-amber)" }}>
              ellestuartauthor.com/essays
            </a>.
          </p>
        </div>
        <Button onClick={newArticle} className="btn-amber rounded-sm"
                data-testid="article-new">
          <Plus size={14} className="mr-2"/> {pendingNewDraft ? "Restore draft" : "New article"}
        </Button>
      </div>

      {pendingNewDraft && (
        <div className="elle-surface-2 p-4 rounded-sm mb-6 flex items-center justify-between gap-4 flex-wrap"
             data-testid="pending-draft-banner"
             style={{ borderLeft: "3px solid var(--elle-amber)" }}>
          <div className="flex items-start gap-3">
            <Archive size={16} style={{ color: "var(--elle-amber)" }}/>
            <div>
              <div className="font-display text-lg" style={{ color: "var(--elle-ink)" }}>
                You have an unsaved draft
              </div>
              <div className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-dim)" }}>
                "{(pendingNewDraft.data.title || "Untitled").slice(0, 80)}" · saved {formatDraftAge(pendingNewDraft.saved_at)}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="btn-amber rounded-sm" onClick={newArticle}
                    data-testid="restore-draft-btn">
              Open
            </Button>
            <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                    data-testid="discard-pending-draft"
                    onClick={() => {
                      clearDraft(draftKey("articles", "new"));
                      setItems(x => x ? [...x] : x);   // nudge memo
                      toast.info("Discarded.");
                    }}>
              Discard
            </Button>
          </div>
        </div>
      )}

      {items === null && <p className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>Loading…</p>}
      {items && items.length === 0 && (
        <div className="elle-surface p-10 rounded-sm text-center">
          <FileText size={32} className="mx-auto mb-3" style={{ color: "var(--elle-ink-faint)" }}/>
          <p style={{ color: "var(--elle-ink-dim)" }}>
            No articles yet. The first piece will live here.
          </p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="elle-surface rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="overline text-left" style={{ color: "var(--elle-ink-dim)" }}>
                <th className="p-3">Title</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Voice</th>
                <th className="p-3">Words</th>
                <th className="p-3">Status</th>
                <th className="p-3">Updated</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.slug} data-testid={`article-row-${a.slug}`}
                    className="border-t" style={{ borderColor: "var(--elle-line)" }}>
                  <td className="p-3">
                    <button onClick={()=>openExisting(a)}
                            className="text-left hover:underline" data-testid={`article-edit-${a.slug}`}
                            style={{ color: "var(--elle-ink)" }}>
                      {a.title}
                    </button>
                    {a.summary && <div className="text-xs mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                      {a.summary.slice(0, 120)}{a.summary.length > 120 ? "…" : ""}
                    </div>}
                  </td>
                  <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{a.kind || "essay"}</td>
                  <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{a.voice}</td>
                  <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{a.word_count}</td>
                  <td className="p-3 font-mono-elle text-xs"
                      style={{ color: a.published ? "var(--elle-moss)" : "var(--elle-ink-faint)" }}>
                    {a.published ? "PUBLISHED" : "DRAFT"}
                  </td>
                  <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>
                    {formatDate(a.updated_at)}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {a.published && (
                      <a href={`/${(a.kind === "excerpt" ? "excerpts" : a.kind === "short_story" ? "stories" : "essays")}/${a.slug}`}
                         target="_blank" rel="noreferrer"
                         className="inline-flex items-center mr-2 text-xs font-mono-elle"
                         style={{ color: "var(--elle-amber)" }}
                         data-testid={`article-view-${a.slug}`}>
                        <ExternalLink size={12} className="mr-1"/>view
                      </a>
                    )}
                    <Button size="sm" variant="outline" className="btn-ghost rounded-sm mr-2"
                            onClick={()=>togglePublish(a)}
                            data-testid={`article-toggle-${a.slug}`}>
                      {a.published ? <><EyeOff size={12} className="mr-1"/>Unpublish</> : <><Eye size={12} className="mr-1"/>Publish</>}
                    </Button>
                    <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                            onClick={()=>remove(a)}
                            data-testid={`article-delete-${a.slug}`}>
                      <Trash2 size={12}/>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
